import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { Product } from '../../../models/Product';
import { VarietySaveRequest, VarietyStatus } from '../../../models/Variety';
import { CatalogoService } from '../../../service/catalogo.service';
import { GeneralService } from '../../../service/general.service';
import { ProductService } from '../../../service/product.service';
import { VarietyService } from '../../../service/variety.service';
import { getCurrentUserId } from '../../../shared/utils/auth-user.util';
import { TransactionAccessService } from '../../../shared/services/transaction-access.service';

@Component({
  selector: 'app-add-variety',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgSelectModule],
  templateUrl: './add-variety.html',
  styleUrls: ['./add-variety.scss'],
})
export class AddVariety implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly varietyService = inject(VarietyService);
  private readonly catalogoService = inject(CatalogoService);
  private readonly productService = inject(ProductService);
  private readonly generalService = inject(GeneralService);
  private readonly router = inject(Router);
  private readonly transactionAccess = inject(TransactionAccessService);

  readonly loading = signal(false);
  readonly success = signal(false);
  readonly error = signal('');
  readonly products = signal<Product[]>([]);
  readonly measurementUnits = signal<any[]>([]);
  readonly sectionName = signal('');
  readonly divisionName = signal('');
  readonly className = signal('');
  readonly statuses = [
    { value: 'A' as VarietyStatus, label: 'Activo' },
    { value: 'I' as VarietyStatus, label: 'Inactivo' }
  ];

  readonly form = this.fb.group({
    sectionId: this.fb.control<number | null>(null, Validators.required),
    divisionId: this.fb.control<number | null>(null, Validators.required),
    classId: this.fb.control<number | null>(null, Validators.required),
    productId: this.fb.control<number | null>(null, Validators.required),
    ineCode: ['', Validators.required],
    name: ['', Validators.required],
    measurementUnitId: this.fb.control<number | null>(null, Validators.required),
    baseQuantity: this.fb.control<number | null>(1, [Validators.required, Validators.min(0.0000001)]),
    ciiuCode: [''],
    naegCode: [''],
    npgCode: [''],
    ccpCode: [''],
    reference: [''],
    status: this.fb.control<VarietyStatus | null>('A', Validators.required)
  });

  ngOnInit(): void {
    forkJoin({
      products: this.productService.getAll(),
      units: this.generalService.get('GET_UNIDADES_MEDIDA')
    }).subscribe({
      next: ({ products, units }) => {
        this.products.set(products ?? []);
        this.measurementUnits.set(units ?? []);
      },
      error: () => this.error.set('No fue posible cargar los catálogos.')
    });
  }

  onProductChange(product: Product | null): void {
    this.clearClassification();
    if (!product?.codigo) {
      this.form.patchValue({ sectionId: null, divisionId: null, classId: null, productId: null });
      return;
    }
    this.form.patchValue({
      sectionId: product.fkSeccion,
      divisionId: product.fkDivision,
      classId: product.fkClase,
      productId: product.codigo
    });
    this.loadClassification(product);
  }

  clearProduct(): void {
    this.onProductChange(null);
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const transactionId = this.transactionAccess.getTransactionId(
      'varieties',
      '/add-variety'
    );
    if (!transactionId) {
      this.error.set(
        'No se encontró la transacción de mantenimiento de variedades asignada al usuario.'
      );
      return;
    }
    const value = this.form.getRawValue();
    const request: VarietySaveRequest = {
      sectionId: value.sectionId!,
      divisionId: value.divisionId!,
      classId: value.classId!,
      productId: value.productId!,
      ineCode: value.ineCode?.trim() ?? '',
      name: value.name?.trim() ?? '',
      measurementUnitId: value.measurementUnitId!,
      baseQuantity: value.baseQuantity!,
      ciiuCode: value.ciiuCode?.trim() ?? '',
      naegCode: value.naegCode?.trim() ?? '',
      npgCode: value.npgCode?.trim() ?? '',
      ccpCode: value.ccpCode?.trim() ?? '',
      reference: value.reference?.trim() ?? '',
      status: value.status!,
      auditUserId: getCurrentUserId(),
      auditTransactionId: transactionId
    };
    this.loading.set(true);
    this.error.set('');
    this.varietyService.add(request).subscribe({
      next: () => {
        this.loading.set(false);
        this.success.set(true);
        this.form.reset({ baseQuantity: 1, status: 'A' });
        this.clearClassification();
      },
      error: (message: string) => {
        this.loading.set(false);
        this.error.set(message);
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/home']);
  }

  private loadClassification(product: Product): void {
    forkJoin({
      sections: this.catalogoService.getSecciones(),
      divisions: this.catalogoService.getDivisiones(product.fkSeccion),
      classes: this.catalogoService.getClases(product.fkDivision)
    }).subscribe({
      next: ({ sections, divisions, classes }) => {
        this.sectionName.set(sections.find(x => x.codigo === product.fkSeccion)?.nombre ?? '');
        this.divisionName.set(divisions.find(x => x.codigo === product.fkDivision)?.nombre ?? '');
        this.className.set(classes.find(x => x.codigo === product.fkClase)?.nombre ?? '');
      },
      error: () => this.error.set('No fue posible cargar la clasificación del producto.')
    });
  }

  private clearClassification(): void {
    this.sectionName.set('');
    this.divisionName.set('');
    this.className.set('');
  }
}
